import Fuse from 'fuse.js'

// Products come from the InstaPunchout catalogs API (uploaded and published in the panel),
// for the connection of the current punchout session.
const API = 'https://punchout.cloud/api/v1/product-catalogs';
export const DEFAULT_IMAGE = '/assets/images/wabtec-logo.svg';

/** onerror for product images: a missing or broken image shows the default logo */
export function imageFallback(ev: Event) {
    const img = ev.target as HTMLImageElement;
    if (!img.src.endsWith(DEFAULT_IMAGE)) img.src = DEFAULT_IMAGE;
}

export async function fetchProducts(punchoutId: string): Promise<Product[]> {
    const res = await fetch(`${API}/session/${encodeURIComponent(punchoutId)}/products`);
    if (!res.ok) {
        throw new Error(`products could not be loaded (${res.status})`);
    }
    const list: any[] = await res.json();
    return list.map((product) => ({
        sku: product.sku,
        image: product.image_url || DEFAULT_IMAGE,
        name: product.name,
        description: product.description || product.name,
        // cents, as the cart has always sent them; price is in thousandths, price_exact keeps
        // prices below a cent (e.g. 0.0107)
        price: (product.price_exact ?? product.price / 1000) * 100,
        unit: product.unit,
        currency: product.currency,
        min_order_quantity: product.min_order_quantity || 1,
        order_increment: product.order_increment || 1,
    } as Product));
}

/** The quantity to order: at least the minimum, then in steps of the order increment. */
export function normalizeQuantity(product: Product, quantity: number): number {
    const min = product.min_order_quantity || 1;
    const step = product.order_increment || 1;
    if (Number.isNaN(quantity) || quantity < min) {
        return min;
    }
    return min + Math.ceil((quantity - min) / step) * step;
}

/** A unit price in cents as dollars: 2 decimals, up to 4 for prices below a cent's precision. */
export function formatPrice(cents: number): string {
    return (cents / 100).toFixed(4).replace(/0{1,2}$/, '');
}


export interface Product {
    category: string;
    image: string;
    brand: string;
    description: any;
    sku: string;
    name: string;
    price: number;
    unit: string;
    currency: string;
    min_order_quantity: number;
    order_increment: number;
}
export interface CartItem {
    id: string;
    product: Product,
    quantity: number,
    options: any,
};

export class Cart {

    items: CartItem[] = [];
}

export class Model {

    cartClass = '';
    search: string = '';
    filter: any = {};

    products: Product[] = [];

    /** loading | ready | no_session | error */
    productsStatus: string = 'loading';
    productsError: string = '';


    cart = new Cart();



    constructor() {
        if (localStorage.getItem('cart')) {
            this.cart = JSON.parse(localStorage.getItem('cart') as any);
        }


    }




}


export type Action
    = { type: 'add', product: Product, quantity: number, options: any }
    | { type: 'remove', product: Product }
    | { type: 'quantity', id: string, quantity: number }
    | { type: 'products', products: Product[] }
    | { type: 'products.status', status: string, error?: string }
    | { type: 'filter', filter: any }
    | { type: 'filter.in', key: string, option: string, value: boolean }
    | { type: 'search', value: string }
    | { type: 'filter.clear' }
    | { type: 'endanimate' }
    | { type: 'clear_cart' };


let fuse = new Fuse([] as Product[]);

export function RootReducer(state: Model, action: Action) {
    console.log(action);
    switch (action.type) {
        case 'clear_cart':
            state.cart.items = [];
            localStorage.setItem('cart', JSON.stringify(state.cart));
            break;
        case 'filter.clear':
            state.search = '';
            state.filter = {};
            break;
        case 'search':
            state.search = action.value;
            break;
        case 'add':
            const id = hash([action.product.sku, action.options]);
            const item = state.cart.items.find(it => it.id == id);
            if (item) {
                item.quantity += action.quantity;
            } else {
                state.cart.items.push({ ...action, id });
            }
            state.cartClass = 'animate__animated animate__headShake';

            localStorage.setItem('cart', JSON.stringify(state.cart));
            break;
        case 'quantity':
            // update quantity
            state.cart.items.filter(it => it.id == action.id).forEach(it => it.quantity = action.quantity);
            // remove if quantity is 0
            state.cart.items = state.cart.items.filter(it => it.quantity);

            localStorage.setItem('cart', JSON.stringify(state.cart));
            break;
        case 'products':
            state.products = [...action.products];
            state.productsStatus = 'ready';
            fuse = new Fuse(state.products, { keys: ['sku', 'name', 'description'], findAllMatches: true, threshold: 0.4 });
            // a cart kept in the browser gets the current prices and rules; products no longer
            // in the catalog are dropped
            state.cart.items = state.cart.items.flatMap(item => {
                const product = state.products.find(it => it.sku == item.product.sku);
                return product ? [{ ...item, product, quantity: normalizeQuantity(product, item.quantity) }] : [];
            });
            localStorage.setItem('cart', JSON.stringify(state.cart));
            break;
        case 'products.status':
            state.productsStatus = action.status;
            state.productsError = action.error || '';
            break;
        case 'filter':
            state.filter = action.filter;
            break;
        case 'filter.in':
            state.filter[action.key] = state.filter[action.key] || [];
            if (action.value) {
                state.filter[action.key].push(action.option);
            } else {
                state.filter[action.key] = state.filter[action.key].filter(it => it !== action.option);
            }
            if (state.filter[action.key].length == 0) {
                delete state.filter[action.key];
            }
            break;
        case 'endanimate':
            state.cartClass = '';
            break;
        default:
            break;
    }
    return { ...state };
}



export function search(state: Model) {
    return (state.search ? fuse.search(state.search).map(it => it.item) : state.products).filter(product => {
        for (const key in state.filter) {
            const value = state.filter[key];
            if (value == undefined) {
                continue;
            } else if (Array.isArray(value)) {
                if (value.includes(product[key])) {
                    continue;
                }
            } else if (value == product[key]) {
                continue;
            }
            return false;
        }
        return true;
    });
}

const hash = (str: any) => {
    if (typeof (str) !== 'string') {
        str = JSON.stringify(str);
    }
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash &= hash; // Convert to 32bit integer
    }
    return new Uint32Array([hash])[0].toString(36);
};


